import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Copy, ExternalLink, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { SortableList } from "@/components/admin/SortableList";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Field } from "./Content";
import { BACKGROUNDS, SECTION_TYPES, newSection } from "./pageSections";

const inp = "w-full border border-stone-300 bg-white rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-crimson/30";

const Select = ({ label, value, options, onChange, testid }) => (
  <div>
    <label className="text-sm font-bold block mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const CardItems = ({ items, onChange, sid }) => (
  <div>
    <label className="text-sm font-bold block mb-2">Cards</label>
    <SortableList items={items} onChange={onChange} handleOnly getKey={(_, i) => i} testid={`${sid}-cards`} render={(c, j) => {
      const set = (k) => (v) => onChange(items.map((x, n) => (n === j ? { ...x, [k]: v } : x)));
      return (
        <div className="space-y-2 py-1">
          <div className="flex gap-2">
            <input value={c.title} onChange={(e) => set("title")(e.target.value)} placeholder="Card title" className={inp} data-testid={`${sid}-card-title-${j}`} />
            <button type="button" onClick={() => onChange(items.filter((_, n) => n !== j))} className="p-2 text-red-500" aria-label="Remove card" data-testid={`${sid}-card-remove-${j}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <textarea value={c.body} onChange={(e) => set("body")(e.target.value)} rows={3} placeholder="Card text" className={inp} data-testid={`${sid}-card-body-${j}`} />
          <ImageUpload value={c.image} onChange={set("image")} testid={`${sid}-card-image-${j}`} />
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={c.link_text} onChange={(e) => set("link_text")(e.target.value)} placeholder="Link text (optional)" className={inp} data-testid={`${sid}-card-link-text-${j}`} />
            <input value={c.link_url} onChange={(e) => set("link_url")(e.target.value)} placeholder="/contact or https://…" className={inp} data-testid={`${sid}-card-link-url-${j}`} />
          </div>
        </div>
      );
    }} />
    <button type="button" onClick={() => onChange([...items, { title: "Card title", body: "", image: "", link_text: "", link_url: "" }])} className="mt-2 text-xs text-crimson font-bold inline-flex items-center gap-1" data-testid={`${sid}-card-add`}><Plus className="w-3.5 h-3.5" />Add card</button>
  </div>
);

const SectionEditor = ({ s, i, update, remove, duplicate }) => {
  const [open, setOpen] = useState(true);
  const sid = `page-s${i}`;
  const set = (k) => (v) => update({ ...s, data: { ...s.data, [k]: v } });
  return (
    <div className="py-1" data-testid={`page-section-${i}`}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left" data-testid={`${sid}-toggle`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="text-xs font-bold uppercase tracking-wide text-crimson">{SECTION_TYPES[s.type]?.label}</span>
          <span className="text-sm text-stone-500 truncate">{s.data.title || ""}</span>
        </button>
        <button type="button" onClick={duplicate} className="p-1.5 text-stone-500 hover:text-charcoal" title="Duplicate" data-testid={`${sid}-duplicate`}><Copy className="w-4 h-4" /></button>
        <button type="button" onClick={remove} className="p-1.5 text-red-500" title="Remove section" data-testid={`${sid}-remove`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="mt-4 space-y-5 border-t border-stone-100 pt-4 pr-2">
          {Object.keys(s.data).map((k) => {
            if (k === "background") return s.type === "hero" ? null : <Select key={k} label="Background" value={s.data[k]} options={BACKGROUNDS} onChange={set(k)} testid={`${sid}-background`} />;
            if (k === "image_side") return <Select key={k} label="Image position" value={s.data[k]} options={[["right", "Image on the right"], ["left", "Image on the left"]]} onChange={set(k)} testid={`${sid}-image_side`} />;
            if (k === "items") return <CardItems key={k} items={s.data.items} onChange={set("items")} sid={sid} />;
            return <Field key={k} section={sid} k={k} value={s.data[k] ?? ""} onChange={set(k)} />;
          })}
        </div>
      )}
    </div>
  );
};

const AddSection = ({ onAdd }) => (
  <div className="bg-white border-2 border-dashed border-stone-300 rounded-lg p-4" data-testid="page-add-section">
    <div className="text-sm font-bold mb-3 flex items-center gap-1.5"><Plus className="w-4 h-4" />Add a section</div>
    <div className="flex flex-wrap gap-2">
      {Object.entries(SECTION_TYPES).map(([type, t]) => (
        <button key={type} type="button" onClick={() => onAdd(type)} className="text-sm border border-stone-300 rounded-full px-3 py-1.5 hover:border-crimson hover:text-crimson" data-testid={`page-add-${type}`}>{t.label}</button>
      ))}
    </div>
  </div>
);

export default function PageEditor() {
  const { id } = useParams();
  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/pages/${id}`).then((r) => alive && setPage((p) => p || r.data)).catch((e) => toast.error(errMsg(e)));
    return () => { alive = false; };
  }, [id]);

  if (!page) return <div className="text-stone-400">Loading…</div>;
  const set = (k) => (v) => setPage((p) => ({ ...p, [k]: v }));
  const setSections = (sections) => setPage((p) => ({ ...p, sections }));

  const save = async (status = page.status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/pages/${id}`, { ...page, status });
      setPage(data);
      toast.success(status === "published" ? "Page saved & published" : "Draft saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const published = page.status === "published";
  return (
    <div data-testid="admin-page-editor">
      <Link to="/admin/pages" className="text-sm text-stone-500 inline-flex items-center gap-1 mb-4 hover:text-charcoal" data-testid="page-back"><ArrowLeft className="w-4 h-4" />All pages</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">{page.title}</h1>
          <p className="text-sm text-stone-500 mt-1">Status: <b className={published ? "text-green-700" : "text-amber-700"} data-testid="page-status">{published ? "Published" : "Draft"}</b></p>
        </div>
        <div className="flex flex-wrap gap-2 sticky top-20">
          <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm border border-stone-300 bg-white rounded-md px-4 py-2.5" data-testid="page-preview-btn"><ExternalLink className="w-4 h-4" />{published ? "View" : "Preview"}</a>
          <button onClick={() => save()} disabled={busy} className="inline-flex items-center gap-1.5 text-sm bg-charcoal text-white rounded-md px-4 py-2.5 font-bold disabled:opacity-60" data-testid="page-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</button>
          <button onClick={() => save(published ? "draft" : "published")} disabled={busy} className={`text-sm rounded-md px-4 py-2.5 font-bold disabled:opacity-60 ${published ? "border border-stone-300 bg-white" : "bg-crimson hover:bg-crimson-hover text-white"}`} data-testid="page-publish-btn">{published ? "Unpublish" : "Publish"}</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <aside className="bg-white border border-stone-200 rounded-lg p-5 space-y-4 lg:sticky lg:top-20" data-testid="page-settings">
          <h2 className="font-bold">Page settings</h2>
          <div><label className="text-sm font-bold block mb-1">Title</label><input value={page.title} onChange={(e) => set("title")(e.target.value)} className={inp} data-testid="page-title-input" /></div>
          <div>
            <label className="text-sm font-bold block mb-1">Page address</label>
            <div className="flex items-center text-sm"><span className="text-stone-400 pr-1">/</span><input value={page.slug} onChange={(e) => set("slug")(e.target.value)} className={inp} data-testid="page-slug-input" /></div>
          </div>
          <div><label className="text-sm font-bold block mb-1">Meta title</label><input value={page.meta_title} onChange={(e) => set("meta_title")(e.target.value)} placeholder={`${page.title} | Indian Hotel Directory`} className={inp} data-testid="page-meta-title" /></div>
          <div><label className="text-sm font-bold block mb-1">Meta description</label><textarea value={page.meta_description} onChange={(e) => set("meta_description")(e.target.value)} rows={3} className={inp} data-testid="page-meta-description" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={published} onCheckedChange={(v) => set("status")(v ? "published" : "draft")} data-testid="page-status-switch" />Published (applies on Save)</label>
        </aside>
        <section className="space-y-4">
          {page.sections.length ? (
            <SortableList items={page.sections} onChange={setSections} handleOnly testid="page-sections" className="space-y-3" render={(s, i) => (
              <SectionEditor s={s} i={i}
                update={(ns) => setSections(page.sections.map((x, n) => (n === i ? ns : x)))}
                remove={() => window.confirm("Remove this section?") && setSections(page.sections.filter((_, n) => n !== i))}
                duplicate={() => setSections([...page.sections.slice(0, i + 1), { ...JSON.parse(JSON.stringify(s)), id: Math.random().toString(36).slice(2, 10) }, ...page.sections.slice(i + 1)])} />
            )} />
          ) : <p className="text-sm text-stone-400 bg-white border border-stone-200 rounded-lg p-8 text-center" data-testid="page-no-sections">This page has no sections yet. Add one below.</p>}
          <AddSection onAdd={(type) => setSections([...page.sections, newSection(type)])} />
        </section>
      </div>
    </div>
  );
}
